import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { AxiosError } from 'axios';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Search, Sidebar } from '@lifeomic/chromicons';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import { DocumentReferenceAttachment } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';
import { DocumentReferenceContent } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/modals/DocumentReferenceModal';
import { useDocumentReference } from '@lifeomic/phc-web-toolkit/src/hooks/useDocumentReference';
import type {
  DocumentExtractionV2Invocation,
  DocumentExtractionV2Output,
  DocumentExtractionV2Status,
} from '@lifeomic/react-client';
import {
  useActiveAccountId,
  useActiveProjectId,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { memberDetailMessages } from './messages';
import {
  formatDocumentExtractionV2Status,
  isDocumentExtractionV2InFlight,
} from './DocumentExtractionV2Tasks';
import LexicalReadOnlyMarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownEditor/Lexical/LexicalReadOnlyMarkdownViewer';
import {
  getObservationMMMYYYYDate,
  getObservationUnit,
  getObservationValueDisplay,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/util/fhir';
import {
  ExtractionFeedbackActions,
  withApprovedExtractionExtensions,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';
import {
  DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION,
  DocumentExtractionV2InvokeBody,
  DocumentExtractionV2SourceResourceType,
  ExtendedDocumentExtractionV2Invocation,
  buildSummaryArtifactResourceIds,
  getSourceReference,
  isTaskForSource,
  selectSourceTask,
} from './documentExtractionV2Source';

const POLL_INTERVAL_MS = 3000;

const documentReferenceFhirService = new FhirService('DocumentReference');
const observationFhirService = new FhirService('Observation');
const provenanceFhirService = new FhirService('Provenance');

const useStyles = makeStyles(theme => ({
  sideBySide: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    height: '100%',
    minHeight: 0,
    width: '100%',
  },
  sourcePane: {
    flex: '1 1 50%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    overflow: 'hidden',
  },
  sourcePaneHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
  },
  sourcePaneTitle: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourcePaneBody: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  sourceMarkdownBody: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
  },
  sourceMarkdownLoading: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
  },
  extractionPane: {
    flex: '1 1 50%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
  },
  extractedObservationHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  extractedObservationSummary: {
    minWidth: 0,
  },
}));

const hasViewableAttachment = (
  document: fhir.DocumentReference | null | undefined,
) => !!document?.content?.[0]?.attachment?.url;

const getAttachmentContentType = (
  document: fhir.DocumentReference | null | undefined,
) =>
  document?.content?.[0]?.attachment?.contentType
    ?.split(';')[0]
    ?.toLowerCase() ?? null;

/**
 * True for any `text/*` source document we want to render inline via the
 * Lexical viewer rather than an iframe. Browsers fall back to a legacy
 * encoding when the signed S3 URL omits `charset=utf-8`, mangling UTF-8
 * bytes (e.g. emoji) into mojibake. We route through Lexical because
 * `Response.text()` always decodes as UTF-8 regardless of headers.
 *
 * Excludes `text/csv` because `DocumentReferenceContent` already routes
 * those through a dedicated CSV viewer. Also matches `text/plain` because
 * many browsers (and some upload UIs) report `.md` files as
 * `text/plain` rather than `text/markdown`.
 */
const isTextSourceRenderableAsMarkdown = (
  document: fhir.DocumentReference | null | undefined,
) => {
  const contentType = getAttachmentContentType(document);
  if (!contentType) {
    return false;
  }
  if (!contentType.startsWith('text/')) {
    return false;
  }
  return contentType !== 'text/csv';
};

const getDocumentTitle = (document: fhir.DocumentReference) =>
  document.type?.text ||
  document.description ||
  document.content?.[0]?.attachment?.title;

const getCommunicationTitle = (communication: fhir.Communication) => {
  const code = communication.meta?.tag?.find(
    tag => tag.system === 'http://lifeomic.com/fhir/fountain-visit-note',
  )?.code;
  const typeLabels: Record<string, string> = {
    nonvisit: 'Non-Visit Note',
    visit: 'Visit Note',
    email: 'Email Note',
  };
  return (
    (code && typeLabels[code]) ||
    code ||
    communication.payload?.[0]?.contentString?.slice(0, 80)
  );
};

const selectSourceExtractionTask = (
  tasks: DocumentExtractionV2Invocation[],
  sourceResourceType: DocumentExtractionV2SourceResourceType,
  sourceResourceId: string,
) => {
  const sourceTasks = tasks
    .filter(task => isTaskForSource(task, sourceResourceType, sourceResourceId))
    .sort((left, right) => right.created - left.created);

  return (
    sourceTasks.find(isDocumentExtractionV2InFlight) ??
    selectSourceTask(tasks, sourceResourceType, sourceResourceId)
  );
};

const decodeBase64Utf8 = (value: string) => {
  const normalizedValue = value.replace(/\s/g, '');
  if (
    !normalizedValue ||
    normalizedValue.length % 4 !== 0 ||
    /[^A-Za-z0-9+/=]/.test(normalizedValue)
  ) {
    return null;
  }

  try {
    const binaryString = window.atob(normalizedValue);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));

    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder('utf-8').decode(bytes);
    }

    return binaryString;
  } catch {
    return null;
  }
};

const getSummaryMarkdown = (document: fhir.DocumentReference | null) => {
  const attachment = document?.content?.[0]?.attachment;
  const contentType = attachment?.contentType?.split(';')[0]?.toLowerCase();

  if (contentType !== 'text/markdown' || !attachment?.data) {
    return null;
  }

  const markdown = decodeBase64Utf8(attachment.data)?.trim();
  return markdown || null;
};

type DuplicateTaskDetail = {
  message?: string;
  task_id?: string;
  status?: DocumentExtractionV2Status;
};

type DocumentExtractionV2Resource = (
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_projections']>[number]
    >
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_artifacts']>[number]
    >
) & {
  dedupe_outcome?: string | null;
  fact_type?: string | null;
  matched_resource_id?: string | null;
  reason?: string | null;
  role?: string | null;
};

type ExtractedResourceItem = {
  key: string;
  label: string;
  details: string[];
};

const isObservationProjection = (resource: DocumentExtractionV2Resource) =>
  resource.resource_type === 'Observation' && !!resource.resource_id;

const getReferenceId = (
  reference: string | undefined,
  resourceType: string,
) => {
  const prefix = `${resourceType}/`;
  return reference?.startsWith(prefix) ? reference.slice(prefix.length) : null;
};

const getObservationIdsFromProvenance = (provenance: fhir.Provenance) =>
  provenance.target
    ?.map(target => getReferenceId(target.reference, 'Observation'))
    .filter((id): id is string => !!id) ?? [];

const getPersistedObservationResourceIds = (
  task: DocumentExtractionV2Invocation | null,
) => {
  const output = getResourceOutput(task);
  const fhirProjections = output?.fhir_projections;
  const projections = Array.isArray(fhirProjections) ? fhirProjections : [];
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts) ? fhirArtifacts : [];
  const ids = [
    ...projections
      .filter(
        projection =>
          isObservationProjection(projection) &&
          ['accepted-new', 'matched-existing'].includes(
            projection.status ?? '',
          ),
      )
      .map(projection => projection.resource_id!),
    ...artifacts
      .filter(
        artifact =>
          isObservationProjection(artifact) && artifact.status === 'persisted',
      )
      .map(artifact => artifact.resource_id!),
  ];

  return Array.from(new Set(ids));
};

const getObservationDisplayName = (observation: fhir.Observation) =>
  observation.code?.text ||
  observation.code?.coding?.find(coding => !!coding.display)?.display ||
  observation.code?.coding?.find(coding => !!coding.code)?.code ||
  'Observation';

const getExtractedObservationValueDisplay = (observation: fhir.Observation) =>
  [getObservationValueDisplay(observation), getObservationUnit(observation)]
    .filter(Boolean)
    .join(' ');

type FindableWindow = Window & {
  find?: (
    searchString: string,
    caseSensitive?: boolean,
    backwards?: boolean,
    wrapAround?: boolean,
    wholeWord?: boolean,
    searchInFrames?: boolean,
    showDialog?: boolean,
  ) => boolean;
};

const getUniqueSearchTerms = (terms: Array<string | null | undefined>) => {
  const seen = new Set<string>();
  return terms
    .map(term => term?.trim())
    .filter((term): term is string => !!term && term.length > 1)
    .filter(term => {
      const key = term.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const getObservationSourceSearchTerms = (observation: fhir.Observation) =>
  getUniqueSearchTerms([
    getExtractedObservationValueDisplay(observation),
    observation.valueQuantity?.value?.toString(),
    observation.valueString,
    getObservationDisplayName(observation),
    observation.code?.coding?.find(coding => !!coding.display)?.display,
    observation.code?.coding?.find(coding => !!coding.code)?.code,
    getObservationMMMYYYYDate(observation),
  ]);

const findTextInElement = (container: HTMLElement, searchText: string) => {
  const document = container.ownerDocument;
  const lowerSearchText = searchText.toLowerCase();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();

  while (currentNode) {
    const nodeText = currentNode.textContent ?? '';
    const matchIndex = nodeText.toLowerCase().indexOf(lowerSearchText);
    if (matchIndex >= 0) {
      const ownerWindow = document.defaultView ?? window;
      const range = new ownerWindow.Range();
      range.setStart(currentNode, matchIndex);
      range.setEnd(currentNode, matchIndex + searchText.length);

      const selection = ownerWindow.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      (currentNode.parentElement ?? container).scrollIntoView({
        block: 'center',
        inline: 'nearest',
      });
      return true;
    }

    currentNode = walker.nextNode();
  }

  return false;
};

const findTextInFrames = (container: HTMLElement, searchText: string) =>
  Array.from(container.querySelectorAll('iframe')).some(iframe => {
    try {
      const frameWindow = iframe.contentWindow as FindableWindow | null;
      frameWindow?.focus();
      return !!frameWindow?.find?.(
        searchText,
        false,
        false,
        true,
        false,
        true,
        false,
      );
    } catch {
      return false;
    }
  });

const findSourceText = (container: HTMLElement | null, searchText: string) => {
  if (container) {
    container.focus();
    if (findTextInElement(container, searchText)) {
      return true;
    }
    if (findTextInFrames(container, searchText)) {
      return true;
    }
  }

  const find = (window as FindableWindow).find;
  return !!find?.(searchText, false, false, true, false, true, false);
};

const getDuplicateTaskDetail = (
  error: AxiosError<{ detail?: DuplicateTaskDetail }>,
): DuplicateTaskDetail | undefined => {
  if (error.response?.status !== 409) {
    return undefined;
  }
  return error.response.data?.detail;
};

const getResourceOutput = (
  task: DocumentExtractionV2Invocation | null,
): DocumentExtractionV2Output | null => task?.output ?? null;

const getResourceLabel = (
  resource: DocumentExtractionV2Resource,
  fallback: string,
) => resource.resource_type ?? fallback;

const getResourceDetails = (resource: DocumentExtractionV2Resource) =>
  [
    resource.status ? `Status: ${resource.status}` : null,
    resource.dedupe_outcome
      ? `Dedupe outcome: ${resource.dedupe_outcome}`
      : null,
    resource.matched_resource_id
      ? `Matched resource ID: ${resource.matched_resource_id}`
      : null,
    resource.fact_type ? `Fact type: ${resource.fact_type}` : null,
    resource.role ? `Role: ${resource.role}` : null,
    resource.reason ? `Reason: ${resource.reason}` : null,
    resource.resource_id ? `Resource ID: ${resource.resource_id}` : null,
  ].filter((detail): detail is string => !!detail);

type DetectedIssueProposalOutput = {
  category?: string;
  severity?: string;
  detail?: string;
  logical_key?: string;
};

const getDetectedIssueProposalItems = (
  task: DocumentExtractionV2Invocation | null,
): ExtractedResourceItem[] => {
  const output = task?.output as
    | (DocumentExtractionV2Output & {
        detectedIssueProposals?: DetectedIssueProposalOutput[];
      })
    | null
    | undefined;
  const proposals = output?.detectedIssueProposals;
  if (!Array.isArray(proposals)) {
    return [];
  }
  return proposals.map((proposal, index) => ({
    key: `detected-issue-proposal-${proposal.logical_key ?? index}`,
    label: proposal.category ?? 'Detected issue proposal',
    details: [
      proposal.severity ? `Severity: ${proposal.severity}` : null,
      proposal.detail ? `Detail: ${proposal.detail}` : null,
      proposal.logical_key ? `Logical key: ${proposal.logical_key}` : null,
    ].filter((detail): detail is string => !!detail),
  }));
};

const getExtractedResourceItems = (
  task: DocumentExtractionV2Invocation | null,
): ExtractedResourceItem[] => {
  const output = getResourceOutput(task);
  const fhirProjections = output?.fhir_projections;
  const projections = Array.isArray(fhirProjections) ? fhirProjections : [];
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts)
    ? fhirArtifacts.filter(
        artifact => artifact.role !== 'summary-document-reference',
      )
    : [];

  return [
    ...projections.map((projection, index) => ({
      key: `projection-${index}-${projection.resource_id ?? 'pending'}`,
      label: getResourceLabel(projection, 'FHIR projection'),
      details: getResourceDetails(projection),
    })),
    ...artifacts.map((artifact, index) => ({
      key: `artifact-${index}-${artifact.resource_id ?? 'pending'}`,
      label: getResourceLabel(artifact, 'FHIR artifact'),
      details: getResourceDetails(artifact),
    })),
  ];
};

const getExtractedObservationResourceItems = (
  observations: fhir.Observation[],
): ExtractedResourceItem[] =>
  observations
    .filter(observation => !!observation.id)
    .map(observation => ({
      key: `provenance-observation-${observation.id}`,
      label: 'Observation',
      details: [`Resource ID: ${observation.id}`],
    }));

const getExtractedResourceMetadata = (
  task: DocumentExtractionV2Invocation | null,
) => {
  const output = getResourceOutput(task);

  if (!output) {
    return [];
  }

  return [
    output.fhir_projection_count != null
      ? `Structured results: ${output.fhir_projection_count}`
      : null,
    output.fhir_projection_status
      ? `Projection status: ${output.fhir_projection_status}`
      : null,
    output.fhir_artifact_count != null
      ? `Persisted FHIR artifacts: ${output.fhir_artifact_count}`
      : null,
    output.artifact_version
      ? `Artifact version: ${output.artifact_version}`
      : null,
  ].filter((detail): detail is string => !!detail);
};

/**
 * Renders a `text/markdown` source DocumentReference via Lexical instead of
 * an iframe. The iframe path served a signed S3 URL with `Content-Type:
 * text/markdown` (no charset), which caused browsers to fall back to a
 * legacy encoding and render UTF-8 emoji as mojibake. `Response.text()`
 * always decodes as UTF-8 per spec, so fetching client-side gives correct
 * bytes regardless of header. Pairs with the file-service fix that appends
 * `;charset=utf-8` to text/* content types.
 */
const MarkdownSourceDocumentViewer: React.FC<{
  document: fhir.DocumentReference;
  bodyClassName: string;
  loadingClassName: string;
  searchRootRef: React.Ref<HTMLDivElement>;
}> = ({ document, bodyClassName, loadingClassName, searchRootRef }) => {
  const { formatMessage } = useIntl();
  const { retrieveAttachment } = useDocumentReference();
  const retrieveAttachmentRef = useRef(retrieveAttachment);
  retrieveAttachmentRef.current = retrieveAttachment;
  const formatMessageRef = useRef(formatMessage);
  formatMessageRef.current = formatMessage;
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const documentId = document.id ?? null;

  useEffect(() => {
    let isCancelled = false;
    setMarkdown(null);
    setErrorMessage(null);

    const load = async () => {
      try {
        const attachmentData = await retrieveAttachmentRef.current(document);
        const downloadUrl: string | undefined = attachmentData?.downloadUrl;
        if (!downloadUrl) {
          if (!isCancelled) {
            setErrorMessage(
              formatMessageRef.current(
                memberDetailMessages.memberDocumentsExtractionSourceLoadError,
              ),
            );
          }
          return;
        }
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Failed to load markdown: ${response.status}`);
        }
        const text = await response.text();
        if (!isCancelled) {
          setMarkdown(text);
        }
      } catch {
        if (!isCancelled) {
          setErrorMessage(
            formatMessageRef.current(
              memberDetailMessages.memberDocumentsExtractionSourceLoadError,
            ),
          );
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [document, documentId]);

  if (errorMessage) {
    return (
      <Box className={bodyClassName}>
        <Text color="negative">{errorMessage}</Text>
      </Box>
    );
  }

  if (markdown === null) {
    return (
      <Box className={loadingClassName}>
        <DotLoader size={1} />
      </Box>
    );
  }

  return (
    <Box
      ref={searchRootRef}
      className={bodyClassName}
      data-testid="document-extraction-v2-source-markdown"
      tabIndex={-1}
    >
      <LexicalReadOnlyMarkdownViewer
        markdown={markdown}
        namespace="document-extraction-v2-source-markdown-viewer"
        syncKey={documentId ?? 'document-extraction-source-markdown'}
      />
    </Box>
  );
};

type DocumentExtractionV2ModalProps = {
  isOpen: boolean;
  subjectId: string;
  onDismiss: () => void;
  document?: fhir.DocumentReference | null;
  communication?: fhir.Communication | null;
};

export const DocumentExtractionV2Modal: React.FC<
  DocumentExtractionV2ModalProps
> = ({
  isOpen,
  document = null,
  communication = null,
  subjectId,
  onDismiss,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const apiClient = useApiClient();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const [notify] = useNotify();
  const [currentTask, setCurrentTask] =
    useState<ExtendedDocumentExtractionV2Invocation | null>(null);
  const [summaryDocument, setSummaryDocument] =
    useState<fhir.DocumentReference | null>(null);
  const [summaryMarkdown, setSummaryMarkdown] = useState<string | null>(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isSourceVisible, setIsSourceVisible] = useState(true);
  const [summaryErrorMessage, setSummaryErrorMessage] = useState<string | null>(
    null,
  );
  const [extractedObservations, setExtractedObservations] = useState<
    fhir.Observation[]
  >([]);
  const [
    provenanceObservationResourceIds,
    setProvenanceObservationResourceIds,
  ] = useState<string[]>([]);
  const [
    extractedObservationsErrorMessage,
    setExtractedObservationsErrorMessage,
  ] = useState<string | null>(null);
  const [isInvoking, setIsInvoking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const notifiedTaskIds = useRef<Set<string>>(new Set());
  const sourceSearchRootRef = useRef<HTMLDivElement | null>(null);

  const sourceResourceType: DocumentExtractionV2SourceResourceType =
    communication ? 'Communication' : 'DocumentReference';
  const sourceResource = communication ?? document;
  const sourceResourceId = sourceResource?.id ?? null;
  const sourceTitle = communication
    ? getCommunicationTitle(communication)
    : document
    ? getDocumentTitle(document)
    : undefined;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCurrentTask(null);
    setSummaryDocument(null);
    setSummaryMarkdown(null);
    setIsSummaryOpen(true);
    setIsSourceVisible(true);
    setSummaryErrorMessage(null);
    setExtractedObservations([]);
    setProvenanceObservationResourceIds([]);
    setExtractedObservationsErrorMessage(null);
    setErrorMessage(null);
  }, [isOpen, sourceResourceId, sourceResourceType]);

  const loadTasks = useCallback(async () => {
    if (!subjectId || !projectId) {
      return [];
    }

    const response = await apiClient.request(
      'GET /v1/agents-v2/document-extraction-v2/tasks',
      {
        subject_id: subjectId,
        project_id: projectId,
        limit: 25,
      },
    );

    return response.data.tasks;
  }, [apiClient, projectId, subjectId]);

  const loadSummaryArtifact = useCallback(
    async (artifactVersion?: string | null) => {
      if (
        !accountId ||
        !sourceResourceId ||
        !projectId ||
        !serviceAuth ||
        !subjectId
      ) {
        return {
          documentReference: null,
          markdown: null,
        };
      }

      const summaryDocumentReferenceIds = await buildSummaryArtifactResourceIds(
        {
          accountId,
          projectId,
          subjectId,
          sourceResourceType,
          sourceResourceId,
          artifactVersion:
            artifactVersion ?? DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION,
        },
      );
      let documentReference: fhir.DocumentReference | null = null;
      for (const summaryDocumentReferenceId of summaryDocumentReferenceIds) {
        try {
          const response = await documentReferenceFhirService.getById(
            serviceAuth,
          )(accountId, summaryDocumentReferenceId);
          documentReference = response.data;
          break;
        } catch (error) {
          if ((error as AxiosError).response?.status !== 404) {
            throw error;
          }
        }
      }

      const markdown = getSummaryMarkdown(documentReference);

      return {
        documentReference,
        markdown,
      };
    },
    [
      accountId,
      projectId,
      serviceAuth,
      sourceResourceId,
      sourceResourceType,
      subjectId,
    ],
  );

  const notifyTerminalTask = useCallback(
    (task: DocumentExtractionV2Invocation) => {
      if (notifiedTaskIds.current.has(task.task_id)) {
        return;
      }

      notifiedTaskIds.current.add(task.task_id);

      if (task.status === 'completed') {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionCompleted,
          ),
          'success',
        );
        return;
      }

      notify(
        formatMessage(memberDetailMessages.memberDocumentsExtractionFailed),
        'error',
      );
    },
    [formatMessage, notify],
  );

  useEffect(() => {
    if (!isOpen || !sourceResourceId) {
      return;
    }

    let isCancelled = false;

    const hydrateCurrentTask = async () => {
      try {
        const tasks = await loadTasks();
        if (isCancelled) {
          return;
        }
        setCurrentTask(
          currentTask =>
            currentTask ??
            selectSourceExtractionTask(
              tasks,
              sourceResourceType,
              sourceResourceId,
            ),
        );
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionLoadError,
            ),
          );
        }
      }
    };

    hydrateCurrentTask();

    return () => {
      isCancelled = true;
    };
  }, [formatMessage, isOpen, loadTasks, sourceResourceId, sourceResourceType]);

  useEffect(() => {
    if (!isOpen || !sourceResourceId) {
      return;
    }

    let isCancelled = false;

    const hydrateSummaryArtifact = async () => {
      try {
        const summaryArtifact = await loadSummaryArtifact();
        if (isCancelled) {
          return;
        }
        setSummaryDocument(summaryArtifact.documentReference);
        setSummaryMarkdown(summaryArtifact.markdown);
        setSummaryErrorMessage(null);
      } catch {
        if (!isCancelled) {
          setSummaryErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionSummaryLoadError,
            ),
          );
        }
      }
    };

    hydrateSummaryArtifact();

    return () => {
      isCancelled = true;
    };
  }, [formatMessage, isOpen, loadSummaryArtifact, sourceResourceId]);

  const invokeExtraction = useCallback(async () => {
    if (!sourceResourceId) {
      return;
    }
    setIsInvoking(true);
    setErrorMessage(null);
    try {
      const invokeBody: DocumentExtractionV2InvokeBody = {
        subject_id: subjectId,
        project_id: projectId,
        source_resource_type: sourceResourceType,
        source_resource_id: sourceResourceId,
        ...(sourceResourceType === 'DocumentReference'
          ? { source_document_reference_id: sourceResourceId }
          : {}),
        include_clinic_only: true,
        force_regenerate: !!summaryDocument,
        reason: 'PHC-UI manual validation',
      };
      const response = (await apiClient.request(
        'POST /v1/agents-v2/document-extraction-v2/invoke',
        invokeBody as Parameters<typeof apiClient.request>[1],
      )) as { data: { task_id: string } };
      const pendingTask: ExtendedDocumentExtractionV2Invocation = {
        task_id: response.data.task_id,
        status: 'pending',
        created: Date.now(),
        subject_id: subjectId,
        project_id: projectId,
        source_document_reference_id:
          sourceResourceType === 'DocumentReference'
            ? sourceResourceId
            : undefined,
        source_resource_type: sourceResourceType,
        source_resource_id: sourceResourceId,
      };
      setCurrentTask(pendingTask);
    } catch (error) {
      const duplicate = getDuplicateTaskDetail(
        error as AxiosError<{ detail?: DuplicateTaskDetail }>,
      );
      if (duplicate?.task_id) {
        const duplicateTask: ExtendedDocumentExtractionV2Invocation = {
          task_id: duplicate.task_id,
          status: duplicate.status ?? 'pending',
          created: Date.now(),
          subject_id: subjectId,
          project_id: projectId,
          source_document_reference_id:
            sourceResourceType === 'DocumentReference'
              ? sourceResourceId
              : undefined,
          source_resource_type: sourceResourceType,
          source_resource_id: sourceResourceId,
        };
        setCurrentTask(duplicateTask);
        setErrorMessage(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionDuplicateTask,
          ),
        );
        return;
      }
      setErrorMessage(
        formatMessage(
          memberDetailMessages.memberDocumentsExtractionInvokeError,
        ),
      );
    } finally {
      setIsInvoking(false);
    }
  }, [
    apiClient,
    formatMessage,
    projectId,
    sourceResourceId,
    sourceResourceType,
    subjectId,
    summaryDocument,
  ]);

  useEffect(() => {
    if (!isOpen || !sourceResourceId || !currentTask) {
      return;
    }

    if (!isDocumentExtractionV2InFlight(currentTask)) {
      return;
    }

    let isCancelled = false;

    const pollCurrentTask = async () => {
      try {
        const tasks = await loadTasks();
        if (isCancelled) {
          return;
        }

        const updatedTask =
          tasks.find(task => task.task_id === currentTask.task_id) ??
          selectSourceExtractionTask(
            tasks,
            sourceResourceType,
            sourceResourceId,
          );

        if (!updatedTask) {
          return;
        }

        if (!isDocumentExtractionV2InFlight(updatedTask)) {
          if (updatedTask.status === 'completed') {
            try {
              const summaryArtifact = await loadSummaryArtifact(
                updatedTask.output?.artifact_version,
              );
              if (isCancelled) {
                return;
              }
              setSummaryDocument(summaryArtifact.documentReference);
              setSummaryMarkdown(summaryArtifact.markdown);
              setSummaryErrorMessage(null);
            } catch {
              if (isCancelled) {
                return;
              }
              setSummaryErrorMessage(
                formatMessage(
                  memberDetailMessages.memberDocumentsExtractionSummaryLoadError,
                ),
              );
            }
          }
          setCurrentTask(updatedTask);
          notifyTerminalTask(updatedTask);
          return;
        }

        setCurrentTask(updatedTask);
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionLoadError,
            ),
          );
        }
      }
    };

    const interval = window.setInterval(pollCurrentTask, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [
    currentTask,
    formatMessage,
    isOpen,
    loadSummaryArtifact,
    loadTasks,
    notifyTerminalTask,
    sourceResourceId,
    sourceResourceType,
  ]);

  const persistedObservationResourceIds = useMemo(
    () => getPersistedObservationResourceIds(currentTask),
    [currentTask],
  );
  const observationResourceIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...persistedObservationResourceIds,
          ...provenanceObservationResourceIds,
        ]),
      ),
    [persistedObservationResourceIds, provenanceObservationResourceIds],
  );
  const observationResourceIdsKey = observationResourceIds.join('|');
  const persistedObservationResourceIdsKey =
    persistedObservationResourceIds.join('|');

  useEffect(() => {
    if (
      !isOpen ||
      !accountId ||
      !sourceResourceId ||
      !projectId ||
      !serviceAuth
    ) {
      return;
    }

    let isCancelled = false;

    const hydrateProvenanceObservationResourceIds = async () => {
      try {
        const response = await provenanceFhirService.search(serviceAuth)(
          accountId,
          projectId,
          {
            entity: getSourceReference(sourceResourceType, sourceResourceId),
            _count: 25,
          },
        );
        if (isCancelled) {
          return;
        }
        const ids = response.data.entry
          ?.flatMap(entry => getObservationIdsFromProvenance(entry.resource))
          .filter((id): id is string => !!id);
        setProvenanceObservationResourceIds(Array.from(new Set(ids ?? [])));
      } catch {
        if (!isCancelled) {
          setProvenanceObservationResourceIds([]);
        }
      }
    };

    hydrateProvenanceObservationResourceIds();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    isOpen,
    projectId,
    serviceAuth,
    sourceResourceId,
    sourceResourceType,
  ]);

  useEffect(() => {
    if (!isOpen || !accountId || !serviceAuth) {
      return;
    }

    if (!observationResourceIds.length) {
      setExtractedObservations([]);
      setExtractedObservationsErrorMessage(null);
      return;
    }

    let isCancelled = false;

    const hydrateExtractedObservations = async () => {
      try {
        const response = await observationFhirService.getByIds(serviceAuth)(
          accountId,
          observationResourceIds,
        );
        if (isCancelled) {
          return;
        }
        setExtractedObservations(
          response.data.entry
            ?.map(entry => entry.resource)
            .filter(
              (resource): resource is fhir.Observation =>
                resource.resourceType === 'Observation',
            ) ?? [],
        );
        setExtractedObservationsErrorMessage(null);
      } catch {
        if (!isCancelled) {
          setExtractedObservations([]);
          setExtractedObservationsErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionObservationsLoadError,
            ),
          );
        }
      }
    };

    hydrateExtractedObservations();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    formatMessage,
    isOpen,
    observationResourceIds,
    observationResourceIdsKey,
    persistedObservationResourceIdsKey,
    serviceAuth,
  ]);

  const isCurrentTaskInFlight = currentTask
    ? isDocumentExtractionV2InFlight(currentTask)
    : false;
  const isAnalyzeDisabled =
    !sourceResourceId || isInvoking || isCurrentTaskInFlight;
  const hasSummaryArtifact = !!summaryDocument;
  const taskExtractedResourceItems = getExtractedResourceItems(currentTask);
  const extractedResourceItems = taskExtractedResourceItems.length
    ? taskExtractedResourceItems
    : getExtractedObservationResourceItems(extractedObservations);
  const detectedIssueProposalItems = getDetectedIssueProposalItems(currentTask);
  const extractedResourceMetadata = getExtractedResourceMetadata(currentTask);
  const hasExtractedContent =
    !!summaryMarkdown ||
    extractedResourceItems.length > 0 ||
    detectedIssueProposalItems.length > 0;
  const modalActions = (
    <>
      <Button variant="outlined" onClick={onDismiss}>
        {formatMessage(memberDetailMessages.memberDocumentsExtractionClose)}
      </Button>
      <Button
        variant="contained"
        onClick={invokeExtraction}
        disabled={isAnalyzeDisabled}
      >
        {isInvoking
          ? formatMessage(
              memberDetailMessages.memberDocumentsExtractionStarting,
            )
          : isCurrentTaskInFlight
          ? formatMessage(
              memberDetailMessages.memberDocumentsExtractionInProgressAction,
            )
          : hasSummaryArtifact
          ? formatMessage(memberDetailMessages.memberDocumentsExtractionRestart)
          : formatMessage(memberDetailMessages.memberDocumentsExtractionStart)}
      </Button>
    </>
  );

  const extractionTitleMessage =
    sourceResourceType === 'Communication'
      ? memberDetailMessages.memberNotesExtractionTitle
      : memberDetailMessages.memberDocumentsExtractionTitle;
  const extractionDescriptionMessage =
    sourceResourceType === 'Communication'
      ? memberDetailMessages.memberNotesExtractionDescription
      : memberDetailMessages.memberDocumentsExtractionDescription;
  const extractionUnknownSourceMessage =
    sourceResourceType === 'Communication'
      ? memberDetailMessages.memberNotesExtractionUnknownNote
      : memberDetailMessages.memberDocumentsExtractionUnknownDocument;
  const extractionModalNoteMessage =
    sourceResourceType === 'Communication'
      ? memberDetailMessages.memberNotesExtractionModalNote
      : memberDetailMessages.memberDocumentsExtractionModalNote;

  const canShowSourceDocument =
    sourceResourceType === 'DocumentReference' &&
    hasViewableAttachment(document);
  const showSideBySide =
    hasExtractedContent && canShowSourceDocument && isSourceVisible;

  const searchSourceForObservation = useCallback(
    (observation: fhir.Observation) => {
      const searchTerms = getObservationSourceSearchTerms(observation);
      if (!searchTerms.length) {
        return;
      }

      setIsSourceVisible(true);
      window.setTimeout(() => {
        const hasMatch = searchTerms.some(term =>
          findSourceText(sourceSearchRootRef.current, term),
        );
        if (!hasMatch) {
          notify(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionSourceSearchNoMatch,
            ),
            'warning',
          );
        }
      });
    },
    [formatMessage, notify],
  );

  const sourceDocumentAttachment = useMemo(
    () =>
      canShowSourceDocument && document
        ? new DocumentReferenceAttachment(document)
        : null,
    [canShowSourceDocument, document],
  );

  const extractionPaneContent = (
    <Box direction="column" gap={2}>
      <Box direction="row" justify="space-between" align="center" gap={1}>
        <Box direction="column" gap={0.5} style={{ minWidth: 0, flex: 1 }}>
          <Text size="body">
            {sourceTitle ?? formatMessage(extractionUnknownSourceMessage)}
          </Text>
          <Text>{formatMessage(extractionDescriptionMessage)}</Text>
        </Box>
        {hasExtractedContent && canShowSourceDocument && !isSourceVisible ? (
          <Button
            variant="text"
            onClick={() => setIsSourceVisible(true)}
            data-testid="document-extraction-v2-show-source"
          >
            {formatMessage(
              memberDetailMessages.memberDocumentsExtractionShowSource,
            )}
          </Button>
        ) : null}
      </Box>

      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      {summaryErrorMessage ? (
        <Text color="negative">{summaryErrorMessage}</Text>
      ) : null}

      <Divider />

      {summaryMarkdown ? (
        <>
          <ExpansionPanel
            ariaOwnsId="document-extraction-v2-summary"
            isOpen={isSummaryOpen}
            onToggle={setIsSummaryOpen}
            title={formatMessage(
              memberDetailMessages.memberDocumentsExtractionSummary,
            )}
          >
            {isSummaryOpen ? (
              <Box style={{ maxHeight: 360, overflow: 'auto' }}>
                <LexicalReadOnlyMarkdownViewer
                  markdown={summaryMarkdown}
                  namespace="document-extraction-v2-summary-viewer"
                  syncKey={
                    summaryDocument?.id ??
                    sourceResourceId ??
                    'document-extraction-summary'
                  }
                />
              </Box>
            ) : null}
          </ExpansionPanel>

          <Divider />
        </>
      ) : null}

      {extractedObservations.length || extractedObservationsErrorMessage ? (
        <>
          <Box direction="column" gap={1}>
            <Text weight="bold">
              {formatMessage(
                memberDetailMessages.memberDocumentsExtractionObservations,
              )}
            </Text>
            {extractedObservationsErrorMessage ? (
              <Text color="negative">{extractedObservationsErrorMessage}</Text>
            ) : null}
            {extractedObservations.map(observation => (
              <Box key={observation.id} direction="column" gap={0.5}>
                <Box className={classes.extractedObservationHeader}>
                  <Box
                    direction="column"
                    gap={0.5}
                    className={classes.extractedObservationSummary}
                  >
                    <Text>{getObservationDisplayName(observation)}</Text>
                    <Text size="caption">
                      {[
                        getExtractedObservationValueDisplay(observation),
                        getObservationMMMYYYYDate(observation),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {observation.id ? (
                      <Text size="caption">
                        {formatMessage(
                          memberDetailMessages.memberDocumentsExtractionObservationResourceId,
                          { id: observation.id },
                        )}
                      </Text>
                    ) : null}
                  </Box>
                  {canShowSourceDocument ? (
                    <Tooltip
                      title={formatMessage(
                        memberDetailMessages.memberDocumentsExtractionFindSource,
                        { label: getObservationDisplayName(observation) },
                      )}
                    >
                      <IconButton
                        icon={Search}
                        size={0}
                        aria-label={formatMessage(
                          memberDetailMessages.memberDocumentsExtractionFindSource,
                          { label: getObservationDisplayName(observation) },
                        )}
                        onClick={() => searchSourceForObservation(observation)}
                        data-testid={`document-extraction-v2-find-source-${observation.id}`}
                      />
                    </Tooltip>
                  ) : null}
                </Box>
                {observation.id ? (
                  <ExtractionFeedbackActions
                    observation={observation}
                    subjectId={subjectId}
                    projectId={projectId}
                    taskId={currentTask?.task_id}
                    onApproved={response => {
                      if (response.approved_by && response.approved_at) {
                        setExtractedObservations(current =>
                          current.map(currentObservation =>
                            currentObservation.id === response.observation_id
                              ? withApprovedExtractionExtensions(
                                  currentObservation,
                                  {
                                    approvedBy: response.approved_by!,
                                    approvedAt: response.approved_at!,
                                  },
                                )
                              : currentObservation,
                          ),
                        );
                      }
                    }}
                    onRejected={observationId => {
                      setExtractedObservations(current =>
                        current.filter(
                          currentObservation =>
                            currentObservation.id !== observationId,
                        ),
                      );
                    }}
                  />
                ) : null}
              </Box>
            ))}
          </Box>
          <Divider />
        </>
      ) : null}

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionResources,
          )}
        </Text>
        {extractedResourceItems.length ? (
          <Box direction="column" gap={1}>
            {extractedResourceItems.map(resource => (
              <Box key={resource.key} direction="column" gap={0.5}>
                <Text>{resource.label}</Text>
                {resource.details.map(detail => (
                  <Text key={detail} size="caption">
                    {detail}
                  </Text>
                ))}
              </Box>
            ))}
          </Box>
        ) : null}
        {extractedResourceMetadata.length ? (
          <Box direction="column" gap={0.5}>
            {extractedResourceMetadata.map(detail => (
              <Text key={detail} size="caption">
                {detail}
              </Text>
            ))}
          </Box>
        ) : null}
        {!extractedResourceItems.length ? (
          <Text>
            {formatMessage(
              memberDetailMessages.memberDocumentsExtractionResourcesEmpty,
            )}
          </Text>
        ) : null}
      </Box>

      {detectedIssueProposalItems.length ? (
        <>
          <Divider />
          <Box
            direction="column"
            gap={1}
            data-testid="document-extraction-v2-detected-issues"
          >
            <Text weight="bold">
              {formatMessage(
                memberDetailMessages.documentExtractionDetectedIssuesTitle,
              )}
            </Text>
            {detectedIssueProposalItems.map(proposal => (
              <Box key={proposal.key} direction="column" gap={0.5}>
                <Text>{proposal.label}</Text>
                {proposal.details.map(detail => (
                  <Text key={detail} size="caption">
                    {detail}
                  </Text>
                ))}
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      <Divider />

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(memberDetailMessages.memberDocumentsExtractionStatus)}
        </Text>
        <Text>
          {currentTask
            ? formatMessage(
                memberDetailMessages.memberDocumentsExtractionStatusValue,
                {
                  status: formatDocumentExtractionV2Status(currentTask.status),
                },
              )
            : formatMessage(
                memberDetailMessages.memberDocumentsExtractionModalStatusHint,
              )}
        </Text>
      </Box>

      {isCurrentTaskInFlight ? (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionInProgressHint,
          )}
        </Text>
      ) : null}

      <Text size="caption">{formatMessage(extractionModalNoteMessage)}</Text>

      {hasExtractedContent ? (
        <Box direction="row" justify="flex-end" gap={1}>
          {modalActions}
        </Box>
      ) : null}
    </Box>
  );

  const sourceDocumentPane =
    showSideBySide && sourceDocumentAttachment && document ? (
      <Box
        className={classes.sourcePane}
        data-testid="document-extraction-v2-source-pane"
      >
        <Box className={classes.sourcePaneHeader}>
          <Text size="subbody" className={classes.sourcePaneTitle}>
            {sourceTitle ??
              formatMessage(
                memberDetailMessages.memberDocumentsExtractionSourceDocument,
              )}
          </Text>
          <Tooltip
            title={formatMessage(
              memberDetailMessages.memberDocumentsExtractionHideSource,
            )}
          >
            <IconButton
              icon={Sidebar}
              size={0}
              color="inverse"
              aria-label={formatMessage(
                memberDetailMessages.memberDocumentsExtractionHideSource,
              )}
              onClick={() => setIsSourceVisible(false)}
              data-testid="document-extraction-v2-hide-source"
            />
          </Tooltip>
        </Box>
        {isTextSourceRenderableAsMarkdown(document) ? (
          <MarkdownSourceDocumentViewer
            document={document}
            bodyClassName={classes.sourceMarkdownBody}
            loadingClassName={classes.sourceMarkdownLoading}
            searchRootRef={sourceSearchRootRef}
          />
        ) : (
          <Box
            ref={sourceSearchRootRef}
            className={classes.sourcePaneBody}
            tabIndex={-1}
          >
            <DocumentReferenceContent
              attachment={sourceDocumentAttachment}
              reference={document}
              disableDeepLink
              disablePdfNav
            />
          </Box>
        )}
      </Box>
    ) : null;

  return (
    <Modal
      data-testid="document-extraction-v2-modal"
      isOpen={isOpen}
      isFullScreen={hasExtractedContent}
      onDismiss={onDismiss}
      title={formatMessage(extractionTitleMessage)}
      size={1}
      actions={hasExtractedContent ? undefined : modalActions}
    >
      {showSideBySide ? (
        <Box className={classes.sideBySide}>
          {sourceDocumentPane}
          <Box className={classes.extractionPane}>{extractionPaneContent}</Box>
        </Box>
      ) : (
        extractionPaneContent
      )}
    </Modal>
  );
};
